import * as React from 'react';
import { IPageGroupConfig, PageConfig, RoutePermission, UserRole } from '../IConfiguration';
import PageHome from './Pages/Content/PageHome';
import Page404 from '../../Common/Pages/Page404';
import PagePlot from './Pages/Dashboard/PagePlot';
import PageFindings from './Pages/Dashboard/PageFindings';
import PageMyData from './Pages/Dashboard/PageMyData';
import PageSiteSelect from './Pages/Dashboard/PageSiteSelect';
import PageLayoutContent from "./PageLayouts/PageLayoutContent";
import PageLayoutDashboard from "./PageLayouts/PageLayoutDashboard";
import { basePageConfig } from "../../Common/BasePageConfig";
import PageLogin from "../../Common/Pages/PageLogin";
import PageRegister from "../../Common/Pages/PageRegister";
import PageSpotAPlatypus from "./Pages/Content/PageSpotAPlatypus";
import PageHowToScan from "./Pages/Content/PageHowToScan";
import PageChooseASite from "./Pages/Content/PageChooseASite";
import PageFAQ from "./Pages/Content/PageFAQ";
import PageAPC from "./Pages/Content/PageAPC";
import PageJoinApmn from "./Pages/Content/PageJoinApmn";
import PagePlotSurvey from "../../Common/Pages/PagePlotSurvey";
import PageWelcome from "./Pages/Dashboard/PageWelcome";
import * as PlatypusMapMarker from '../../../Map/Platypus/MapMarker';
import PagePreRegister from './Pages/Dashboard/PagePreRegister';
import PageUserProfile from '../../Common/Pages/PageUserProfile';
import PageLegalPrivacy from './Pages/Content/PageLegalPrivacy';
import InternalAbsoluteLink from '../../Common/Links/InternalAbsoluteLink';
import PageAdminDashboard from '../../Common/AdminPages/PageAdminDashboard';
import PageAdminPlots from '../../Common/AdminPages/PageAdminPlots';
import PageAdminPlotEdit from '../../Common/AdminPages/PageAdminPlotEdit';
import PageAdminUsers from '../../Common/AdminPages/PageAdminUsers';
import PageAdminUserEdit from '../../Common/AdminPages/PageAdminUserEdit';
import TestPage from '../../Common/Pages/TestPage';
import PagePasswordReset from '../../Common/Pages/PagePasswordReset';
import PageActivateAccount from '../../Common/Pages/PageActivateAccount';
import PageActivateAccountResend from '../../Common/Pages/PageActivateAccountResend';
import PageHelp from './Pages/Dashboard/PageHelp';
import { Link } from 'react-router-dom';
import PageAdminImports from '../../Common/AdminPages/PageAdminImports';
import PageAdminFiles from '../../Common/AdminPages/PageAdminFiles';
import PageAdminExports from '../../Common/AdminPages/PageAdminExports';
import PageAdminTemplates from '../../Common/AdminPages/PageAdminTemplates';
import AdminContactEmailLink from '../../../Content/AdminContactEmailLink';

export interface IHiddenFieldOption {
    value: number;
    label: string;
    dbValue: string;
}

const googleApiURL: string = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyAWpqvFDJ_faZ_OYjsGicI0_epY-NsLfDY&libraries=geometry,places,visualization';

const circleIcon: PlatypusMapMarker.MarkerIcon = {
    path: "M-20,0 a20,20 0 1,0 40,0 a20,20 0 1,0 -40,0",
    fillOpacity: 1,
    anchor: { x: 0, y: 0 },
    strokeWeight: 2,
    scale: 1
}
const teardropIcon: PlatypusMapMarker.MarkerIcon = {
    anchor: { x: 15, y: 3 },
    rotation: 180,
    path: "M15 3 Q16.5 6.8 25 18 A12.8 12.8 0 1 1 5 18 Q13.5 6.8 15 3z",
    fillOpacity: 1,
    scale: 1.5,
    strokeWeight: 1.5
}

const seenPlatypusOptions: IHiddenFieldOption[] = [{
    value: 0,
    label: "Never seen in the wild",
    dbValue: "Never seen in the wild",
},
{
    value: 1,
    label: "1-3 sightings",
    dbValue: "1-3 sightings"
},
{
    value: 2,
    label: "4-10 sightings",
    dbValue: "4-10 sightings"
},
{
    value: 3,
    label: ">10 sightings",
    dbValue: ">10 sightings"
}];

const pagesDashboard: PageConfig[] = [
    {
        type: "LINK",
        name: "Home",
        route: "/home",
        generateNav: true,
        icon: "fa-home",
    },
    {
        type: "PAGE",
        name: "Plot",
        route: [
            "plot/:plotId",
            "site/:plotId"
        ],
        component: PagePlot,
        configurationProps: {
            mapGoogleMapURL: googleApiURL,
            mapMarkerComponent: PlatypusMapMarker.default,
        },
        generateNav: false
    },
    {
        type: "PAGE",
        name: "Findings",
        route: ["findings"],
        component: PageFindings,
        configurationProps: {
            defaultMarkerIcon: teardropIcon,
            mapType: "clustered",
            mapGridSize: 30,
            mapGoogleMapURL: googleApiURL,
            mapMarkerComponent: PlatypusMapMarker.default,
            mapMarkerOutlineColour: '#000000'
        },
        generateNav: true,
        generateSideBar: true,
        generateLegendFindings: true,
        icon: "fa-map"
    },
    {
        type: "PAGE",
        name: "My Data",
        route: ["my-data"],
        component: PageMyData,
        configurationProps: {
            //onItemClickedRoute: p => {return `plot/${p.plot.id}`}
        },
        generateNav: true,
        generateNavIfLoggedIn: true,
        icon: "fa-columns",
        routePermission: RoutePermission.LOGGED_IN_ONLY,
        routePermissionRedirectTo: "/user/login"
    },
    {
        type: "PAGE",
        name: "Site Selection",
        route: ["site-select"],
        component: PageSiteSelect,
        configurationProps: {
            defaultMarkerIcon: teardropIcon,
            mapType: "clustered",
            mapGridSize: 30,
            mapGoogleMapURL: googleApiURL,
            mapMarkerComponent: PlatypusMapMarker.default,
            mapMarkerOutlineColour: '#E6E4E6'
        },
        generateNav: true,
        generateLegendSiteSelect: true,
        generateNavIfLoggedIn: true,
        icon: "fa-map-marker",
        routePermission: RoutePermission.LOGGED_IN_ONLY,
        routePermissionRedirectTo: "/user/login"
    },
    {
        type: "PAGE",
        name: "Add data",
        route: [
            "plot/:plotId/add-data", 
            "site/:plotId/add-data"
        ],
        component: PagePlotSurvey,
        configurationProps: {
            onSubmitSurveyRoute: p => { return `/plot/${p.routeParams.plotId}` },
            mapMarkerComponent: PlatypusMapMarker.default,
            preamble: p => { return <div className="my-3 py-2 border d-flex flex-row">
                            <i className="fa fa-info-circle px-3"/>
                            <div>If you'd like to report any special observations (e.g. unusual platypus behaviour, finding illegal nets or a dead platypus), or request a change to a site, click <AdminContactEmailLink subject={`Site:%20${p.routeParams.plotId}`} title="here"/>.</div>
                        </div>}
        },
        generateNav: false,
        routePermission: RoutePermission.LOGGED_IN_ONLY,
        routePermissionRedirectTo: "/user/login"
    },
    {
        type: "PAGE",
        name: "Login",
        route: ["user/login"],
        component: PageLogin,
        configurationProps: {
            children: [
                <p key="login-1">Forgot your username or password? {
                    InternalAbsoluteLink(
                        "/user/passwordreset",
                        "CLICK HERE",
                        undefined,
                        "")}
                </p>,
                <p key="login-2">You cannot login and upload data until you have registered as an APMN volunteer.
                    <br />
                    For more information {
                        InternalAbsoluteLink(
                            "/join-apmn",
                            "CLICK HERE",
                            undefined,
                            "")}
                </p>
            ],
            redirectEmailNotVerifiedRoute: "/user/activate/resend",
            registerRoute: "/join-apmn",
            title: "Volunteer Login",

        },
        generateNav: false,
        routePermission: RoutePermission.LOGGED_OUT_ONLY,
        routePermissionRedirectTo: "/user"
    },
    {
        type: "PAGE",
        name: "Register",
        route: ["user/register"],
        component: PageRegister,
        configurationProps: {
            formItems: [{
                maxLength: 20,
                model: ".postcode",
                required: true,
                title: "Post Code"
            },
            {
                maxLength: 20,
                model: ".mobileNumber",
                required: false,
                title: "Mobile Number"
            }],
            hiddenFieldModel: ".platypusSeen",
            hiddenFieldOptions: seenPlatypusOptions,
            title: "Volunteer Registration",
            userAgreementTerms: <span>
                <p>The Australian Platypus Conservancy takes its privacy obligations seriously in accordance with the national standards set out by the Australian Privacy Principles. We will only use your personal information to help administer this website and communicate with you, and will not disclose it or use it for any other purpose without your consent, unless there is a specific legal reason or requirement to do so (for example, to enable a potentially unlawful act to be investigated by a law enforcement agency).</p>
                <p>If you submit material for inclusion on this website, you agree to grant a perpetual, royalty-free, non-exclusive and irrevocable license to the Australian Platypus Conservancy to use, copy, analyse, distribute, publish or display this material for research or improved platypus management purposes. You also grant us the right to keep a record of associated details of your submission, including personal details, in order to monitor use of the website and compliance with its terms.</p>
                <p>As a condition of your use of this website, you agree not to use the website in any way that is unlawful or otherwise prohibited by its own terms and conditions. In particular, you agree not to submit any material for inclusion on this website that infringes the intellectual property rights of someone else or is misleading or fraudulent, to attempt to interfere with proper functioning of the website or its use by others, or to attempt to compromise the security of the web server. If you breach the terms of this paragraph, the Australian Platypus Conservancy reserves the right to report your actions to the relevant authorities and/or prohibit your use of the website.</p>
            </span>,
            userAgreementCheckboxText: "Accept Terms and Conditions of Use"
        },
        generateNav: false,
        routePermission: RoutePermission.LOGGED_OUT_ONLY,
        routePermissionRedirectTo: "/findings"
    },
    {
        type: "PAGE",
        name: "Password Reset",
        route: ["user/passwordreset"],
        component: PagePasswordReset,
        configurationProps: {
            passwordResetBody: <p>Please enter the <strong>email address</strong> you used to sign up. You will be emailed a link to reset your password.</p>
        },
        generateNav: false
    },
    {
        type: "PAGE",
        name: "Password Reset",
        route: ["user/activateaccount"],
        component: PageActivateAccount,
        configurationProps: {
        },
        generateNav: false,
        routePermission: RoutePermission.LOGGED_OUT_ONLY,
        routePermissionRedirectTo: "/user"
    },
    {
        type: "PAGE",
        name: "Password Reset",
        route: ["user/activate/resend"],
        component: PageActivateAccountResend,
        configurationProps: {
        },
        generateNav: false,
        routePermission: RoutePermission.LOGGED_OUT_ONLY,
        routePermissionRedirectTo: "/user"
    },
    {
        type: "PAGE",
        name: "Register",
        route: ["welcome"],
        component: PageWelcome,
        configurationProps: {
        },
        generateNav: false
    },
    {
        type: "PAGE",
        name: "Help",
        route: ["help", "help/:helpSection"],
        component: PageHelp,
        configurationProps: {
        },
        generateNav: true,
        generateNavIfLoggedIn: true,
        icon: "fa-question-circle",
    },
    {
        type: "PAGE",
        name: "Pre-Register",
        route: ["register"],
        component: PagePreRegister,
        configurationProps: {
            seenPlatypusOptions: seenPlatypusOptions
        },
        generateNav: false,
        routePermission: RoutePermission.LOGGED_OUT_ONLY,
        routePermissionRedirectTo: "/findings"
    },
    {
        type: "PAGE",
        name: "Profile",
        route: ["user"],
        component: PageUserProfile,
        configurationProps: {
            formItems: [{
                model: ".postcode",
                userProp: "postcode",
                required: true,
                title: "Post Code"
            },
            {
                model: ".mobileNumber",
                userProp: "mobileNumber",
                required: false,
                title: "Mobile Number"
            }],
        },
        generateNav: false,
        routePermission: RoutePermission.LOGGED_IN_ONLY,
        routePermissionRedirectTo: "/user/login"
    },
    {
        type: "PAGE",
        name: "Administration",
        route: ["admin"],
        component: PageAdminDashboard,
        configurationProps: {
        },
        generateNav: true,
        generateNavIfHasRole: UserRole.ADMIN,
        icon: "fa-rocket",
        routeRole: UserRole.ADMIN
    },
    {
        type: "PAGE",
        name: "Administration - Template Management",
        route: [
            "admin/templates"
        ],
        component: PageAdminTemplates,
        configurationProps: {
        },
        generateNav: false,
        routeRole: UserRole.ADMIN
    },
    {
        type: "PAGE",
        name: "Administration - Site Management",
        route: [
            "admin/plot", 
            "admin/site"
        ],
        component: PageAdminPlots,
        configurationProps: {
        },
        generateNav: false,
        routeRole: UserRole.ADMIN
    },
    {
        type: "PAGE",
        name: "Administration - Site Management",
        route: [
            "admin/plot/:plotId", 
            "admin/site/:plotId"
        ],
        component: PageAdminPlotEdit,
        configurationProps: {
        },
        generateNav: false,
        routeRole: UserRole.ADMIN
    },
    {
        type: "PAGE",
        name: "Administration - User Management",
        route: ["admin/user"],
        component: PageAdminUsers,
        configurationProps: {
        },
        generateNav: false,
        routeRole: UserRole.ADMIN
    },
    {
        type: "PAGE",
        name: "Administration - User Management",
        route: ["admin/user/:userId"],
        component: PageAdminUserEdit,
        configurationProps: {
        },
        generateNav: false,
        routeRole: UserRole.ADMIN
    },
    {
        type: "PAGE",
        name: "Administration - File Management",
        route: ["admin/file"],
        component: PageAdminFiles,
        configurationProps: {
        },
        generateNav: false,
        routeRole: UserRole.ADMIN
    },
    {
        type: "PAGE",
        name: "Administration - Site Exports",
        route: [
            "admin/export/plot", 
            "admin/export/site"
        ],
        component: PageAdminExports,
        configurationProps: {
        },
        generateNav: false,
        routeRole: UserRole.ADMIN
    },
    {
        type: "PAGE",
        name: "Administration - Imports",
        route: [
            "admin/plot/:plotId/import", 
            "admin/site/:plotId/import"
        ],
        component: PageAdminImports,
        configurationProps: {
        },
        generateNav: false,
        routeRole: UserRole.ADMIN
    },
    /*{
        type: "PAGE",
        name: "Test Page",
        route: ["test"],
        component: TestPage,
        configurationProps: {
        },
        generateNav: true,
        icon: "fa-spin fa-gear"
    }*/
]

const pagesContent: PageConfig[] = [
    {
        type: "PAGE",
        name: "404",
        route: ["404"],
        component: Page404,
        configurationProps: {

        },
        generateNav: false
    },
    {
        type: "PAGE",
        name: "Home",
        route: ["home"],
        component: PageHome,
        configurationProps: {
        },
        generateNav: true
    },
    {
        type: "PAGE",
        name: "Things to consider before you join APMN",
        route: ["join-apmn"],
        component: PageJoinApmn,
        configurationProps: {
        },
        generateNav: true
    },
    {
        type: "PAGE",
        name: "How to spot a Platypus in the Wild",
        route: ["spot-a-platypus"],
        component: PageSpotAPlatypus,
        configurationProps: {
        },
        generateNav: true
    },
    {
        type: "PAGE",
        name: "Monitoring Methods",
        route: ["how-to-scan"],
        component: PageHowToScan,
        configurationProps: {
        },
        generateNav: true
    },
    {
        type: "PAGE",
        name: "Choosing a monitoring site",
        route: ["choose-monitoring-site"],
        component: PageChooseASite,
        configurationProps: {
        },
        generateNav: true
    },
    {
        type: "PAGE",
        name: "FAQs",
        route: ["faq"],
        component: PageFAQ,
        configurationProps: {
        },
        generateNav: true
    },
    {
        type: "PAGE",
        name: "Legal/Privacy Policy",
        route: ["legal-privacy-policy"],
        component: PageLegalPrivacy,
        configurationProps: {
        },
        generateNav: false
    },
    {
        type: "PAGE",
        name: "Australian Platypus Conservancy",
        route: ["australian-platypus-conservancy"],
        component: PageAPC,
        configurationProps: {
        },
        generateNav: true
    },
    {
        type: "LINK",
        name: "Registered Volunteer Login",
        route: "/user/login",
        generateNav: true
    },
];

export const groups: IPageGroupConfig[] = [
    {
        name: "dashboard",
        theme: "platypus",
        classification: "platypus",
        classificationRoute: "",
        pageLayout: {
            component: PageLayoutDashboard
        },
        pages: basePageConfig.concat(pagesDashboard)
    },
    {
        name: "content",
        theme: "platypus",
        classification: "platypus",
        classificationRoute: "",
        pageLayout: {
            component: PageLayoutContent
        },
        pages: pagesContent
    }
];

